import { forwardRef, useEffect, useRef } from "react";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface PlayerProps {
  id: string;
  file: string;
  hls?: number;
  preload?: string;
  playsinline?: boolean;
  onProgressThreshold?: () => void;
  progressThreshold?: number;
  duration?: number | null;
  height?: number | null;
  width?: number;
  isMobile?: boolean;
  crossOrigin?: string;
  withCredentials?: boolean;
  autoplay?: number | null;
  poster?: string;
}

interface PlayerInstance {
  api: (command: string) => number;
  destroy: () => void;
}

interface PlayerConfig {
  id: string;
  file: string;
  hls?: number;
  preload?: string;
  playsinline: boolean;
  autoplay: number;
  poster?: string;
  headers: {
    Origin: string;
    'Access-Control-Request-Method': string;
  };
  onError: (error: Error) => void;
  onReady: () => void;
}

interface PlayerJSConstructor {
  new(config: PlayerConfig): PlayerInstance;
}

declare global {
  interface Window {
    Playerjs?: PlayerJSConstructor;
    PlayerjsEvents?: (event: string, playerId: string, data: number) => void;
  }
}

const Player = forwardRef<PlayerInstance, PlayerProps>(({
  id,
  file,
  hls,
  preload,
  playsinline,
  onProgressThreshold,
  progressThreshold = 1.0, // Default to 100% if no threshold is provided
  duration,
  height,
  width,
  isMobile,
  autoplay,
  poster
}, ref) => {
  const hasTriggeredThresholdRef = useRef(false);
  const isPlayerReadyRef = useRef(false);
  const playerInstanceRef = useRef<string>(file); // Track current file for cleanup
  const internalPlayerRef = useRef<PlayerInstance | null>(null);

  useEffect(() => {
    const handlePlayerEvents = (event: string, playerId: string, data: number) => {
      // console.log("PlayerJS Event:", {
      //   event,
      //   playerId,
      //   data,
      //   hasTriggeredThreshold: hasTriggeredThresholdRef.current,
      // });
      if (event === "time" && internalPlayerRef.current) {
        try {
          const videoDuration = duration || internalPlayerRef.current.api("duration");
          // console.log("Video Duration:", videoDuration);
          // console.log("Current Time:", data);
          // console.log("Progress Threshold:", progressThreshold);
          // console.log(hasTriggeredThresholdRef.current, "current thresholdref");
          // Ensure videoDuration is a positive number
          if (videoDuration > 0 && !hasTriggeredThresholdRef.current) {
            const progress = data / videoDuration;
            // Ensure the threshold hasn't been triggered before
            const isThresholdReached =
              progress >= progressThreshold &&
              !hasTriggeredThresholdRef.current;

            if (isThresholdReached) {
              // console.log('Threshold Reached Details:', {
              //   currentTime: data,
              //   duration: videoDuration,
              //   progress: progress,
              //   threshold: progressThreshold
              // });

              // Set the flag to prevent multiple triggers
              hasTriggeredThresholdRef.current = true;
              // Explicitly Trigger the callback
              onProgressThreshold?.();

              if (internalPlayerRef.current?.api) {
                internalPlayerRef.current.api('play');
              }
            }
          }
        } catch (error) {
          console.error('Progress calculation error:', error);
        }
      }
    };

    // Attach the event handler to window
    window.PlayerjsEvents = handlePlayerEvents;

    const initializePlayer = () => {
      const PlayerJS = window.Playerjs;
      if (PlayerJS) {
        try {
          // Cleanup existing player instance if file has changed
          if (internalPlayerRef.current && playerInstanceRef.current !== file) {
            if (typeof internalPlayerRef.current.destroy === 'function') {
              internalPlayerRef.current.destroy();
            }
            internalPlayerRef.current = null;
            hasTriggeredThresholdRef.current = false;
          }

          if (!internalPlayerRef.current) {
            // Check both prop and localStorage for autoplay
            const shouldAutoplayFromStorage = localStorage.getItem('shouldAutoplay') === 'true';
            const finalAutoplayValue = autoplay || (shouldAutoplayFromStorage ? 1 : 0);

            // Clear localStorage after reading
            localStorage.removeItem('shouldAutoplay');
            localStorage.removeItem('selectedVideoId');

            internalPlayerRef.current = new PlayerJS({
              id,
              file,
              hls,
              preload,
              playsinline: true,
              autoplay: finalAutoplayValue,
              poster: poster,
              headers: {
                'Origin': window.location.origin,
                'Access-Control-Request-Method': 'GET'
              },
              onError: (error: Error) => {
                console.error('Playerjs Error:', error);
              },
              onReady: () => {
                // Set player ready state 
                isPlayerReadyRef.current = true;
                playerInstanceRef.current = file;
                // console.log('Player initialized with autoplay:', autoplayValue);

                // If autoplay is enabled (from either source), explicitly start playing
                if (finalAutoplayValue) {
                  setTimeout(() => {
                    if (internalPlayerRef.current?.api) {
                      internalPlayerRef.current.api('play');
                    }
                  }, 500);
                }
              }
            });

            if (typeof ref === 'function') {
              ref(internalPlayerRef.current);
            } else if (ref) {
              ref.current = internalPlayerRef.current;
            }
          }
        } catch (error) {
          console.error('Playerjs initialization error:', error);
        }
      }
    };

    // Load player script if not already loaded
    if (!document.querySelector(`script[src*="playerjs"]`)) {
      const script = document.createElement("script");
      script.src = "/scripts/playerjs.js";
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        // console.log('PlayerJS script loaded successfully');
        initializePlayer();
      };

      script.onerror = () => {
        console.error('Failed to load PlayerJS script');
      };
    } else {
      initializePlayer();
    }

    // Cleanup function
    return () => {
      if (internalPlayerRef.current?.destroy) {
        internalPlayerRef.current.destroy();
        internalPlayerRef.current = null;
      }
      window.PlayerjsEvents = undefined;
    };
  }, [
    id,
    file,
    hls,
    preload,
    playsinline,
    progressThreshold,
    onProgressThreshold,
    duration,
    isMobile,
    autoplay,
    ref,
    poster
  ]);

  return (
    <div
      id={id}
      style={{
        width: isMobile ? '100%' : `${width}px`,
        height: isMobile ? '200px' : `${height}px`,
        maxWidth: '100%',
        borderRadius: "8px",
        border: "1px solid rgba(0, 0, 0, 0.12)"
      }}
    />
  );
});

Player.displayName = 'Player';

export default Player;